/**
 * The body as every step of the recovery chain asks of it, answered from one read.
 *
 * A step used to ask Word: which paragraph carries this id, which paragraphs stand either
 * side of it, which paragraph is at this index of the walk, the whole body for the server.
 * Each question was a round trip of its own, and a recovery asked tens of them for one lost
 * card. The body of the document is already in hand when a recovery runs (`RecoveryBodyRead`,
 * one sync), so every one of those questions is answered from it, and a step asks Word nothing.
 *
 * The read is a picture of the document at one moment: a recovery lays bookmarks, which move
 * no text, so the read stays true across every recovery of one batch. When the document's
 * text changed after the read began, the caller reads again (`AnchorTracker.recoverLostTogether`).
 */
import type { BodyAnchorParagraph, RecoveryBodyRead, WordParagraph } from "../word-document";

export class RecoveryBody {
    /** Word's paragraph list position for each `uniqueLocalId`, so a lookup by id and a step to either neighbour are one map read. */
    private readonly listIndexByUniqueLocalId = new Map<string, number>();

    constructor(private readonly bodyRead: RecoveryBodyRead) {
        bodyRead.wordParagraphs.forEach((wordParagraph, listIndex) => this.listIndexByUniqueLocalId.set(wordParagraph.uniqueLocalId, listIndex));
    }

    /** Every walk paragraph with the ids, accepted text and heading path the reader gives it, for the card-anchor checks. */
    get anchorParagraphs(): BodyAnchorParagraph[] {
        return this.bodyRead.anchorParagraphs;
    }

    /** The body as a flat OPC package, what the server resolver reads. */
    get bodyFlatOpc(): string {
        return this.bodyRead.bodyFlatOpc;
    }

    /**
     * The paragraph Word lists under this id, with the text Word shows in it.
     *
     * NULL
     *   The paragraph is not in the body: the user deleted it, or the id was never this document's.
     *
     * EXAMPLE
     *   list [p-1 "Intro", p-2 "Fees"], id "p-2" -> { p-2, "Fees" }; id "p-9" -> null
     */
    paragraphById(uniqueLocalId: string): WordParagraph | null {
        const listIndex = this.listIndexByUniqueLocalId.get(uniqueLocalId);
        /** CASES: the id is in Word's list -> its paragraph; it is not -> null. */
        if (listIndex === undefined) return null;
        return this.bodyRead.wordParagraphs[listIndex];
    }

    /**
     * The paragraphs directly after and before `paragraph`, those that exist, the one after first.
     *
     * CASES
     *   - the paragraph is in the body, with one on each side -> [after, before]
     *   - it is the first or the last                         -> the one side that exists
     *   - it is not in the body                               -> none
     *
     * EXAMPLE
     *   list [p-1, p-2, p-3], paragraph p-2 -> [p-3, p-1]; paragraph p-1 -> [p-2]
     */
    neighboursOf(paragraph: WordParagraph): WordParagraph[] {
        const listIndex = this.listIndexByUniqueLocalId.get(paragraph.uniqueLocalId);
        /** WHEN the paragraph is not in Word's list CONSEQUENCE it has no neighbours to name. */
        if (listIndex === undefined) return [];
        const paragraphAfter: WordParagraph | undefined = this.bodyRead.wordParagraphs[listIndex + 1];
        const paragraphBefore: WordParagraph | undefined = this.bodyRead.wordParagraphs[listIndex - 1];
        return [paragraphAfter, paragraphBefore].filter((neighbour): neighbour is WordParagraph => neighbour !== undefined);
    }

    /**
     * The paragraphs at the core's indexes, positions in the body's OOXML walk, with the walk's
     * accepted text.
     *
     * CASES
     *   - the index names a paragraph Word lists                -> that paragraph
     *   - the index names one Word lists no paragraph for       -> nothing for it
     *   - the index is past the walk's end                      -> nothing for it
     *
     * EXAMPLE
     *   walk [W0, W1, null, W3], indexes [1, 2, 9] -> [W1]
     */
    paragraphsAt(walkIndexes: number[]): WordParagraph[] {
        return walkIndexes.flatMap((walkIndex) => this.bodyRead.wordParagraphByWalkIndex[walkIndex] ?? []);
    }
}
